import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Sliders, ChevronRight, History, CheckCircle2, AlertTriangle } from "lucide-react";

const DIRECTION_TONE = {
  maturity: "border-good/40 bg-good-soft text-good",
  risk:     "border-warn/40 bg-warn-soft text-warn",
};

export default function FrameworkEditorList() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/admin/framework-editor/frameworks");
        setItems(data.items || []);
      } catch (ex) {
        toast.error(formatApiErrorDetail(ex.response?.data?.detail) || ex.message);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  return (
    <div className="space-y-6" data-testid="framework-editor-list-page">
      <div className="border-b border-ink-line pb-6">
        <div className="text-[11px] uppercase tracking-[0.2em] text-ink-muted">Admin · Versioned Schema</div>
        <h1 className="font-display text-4xl font-bold tracking-tight mt-1 flex items-center gap-2">
          <Sliders className="w-7 h-7 text-brand" /> Framework Questions
        </h1>
        <p className="text-sm text-ink-muted mt-2 max-w-3xl">
          Manage the question, option, and score schema for each assessment framework.
          Saving creates a new version that applies to new assessments straight away.
          Scores already submitted are stored and never change.
        </p>
      </div>

      {loading ? (
        <div className="text-sm text-ink-muted uppercase tracking-wider">Loading frameworks…</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="framework-list-grid">
          {items.map((f) => (
            <Link
              key={f.framework}
              to={f.editable ? `/admin/framework-editor/${f.framework.toLowerCase().replace("_", "-")}` : "#"}
              data-testid={`framework-card-${f.framework}`}
              className={`card-flat p-6 group ${f.editable ? "hover:-translate-y-0.5 hover:shadow-md transition-all" : "opacity-70 cursor-not-allowed"}`}
              onClick={(e) => { if (!f.editable) e.preventDefault(); }}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-display text-xl font-bold tracking-tight">{f.framework}</span>
                    <span className={`chip text-[10px] ${DIRECTION_TONE[f.direction] || "border-ink-line bg-secondary text-ink-muted"}`}>
                      {f.direction === "risk" ? "Risk-direction" : "Maturity-direction"}
                    </span>
                    {f.editable ? (
                      <span className="chip border-brand/40 bg-brand-soft text-brand text-[10px]">
                        <CheckCircle2 className="w-3 h-3 inline mr-0.5" /> Editable
                      </span>
                    ) : (
                      <span className="chip border-ink-line bg-secondary text-ink-muted text-[10px]">
                        <AlertTriangle className="w-3 h-3 inline mr-0.5" /> View-only
                      </span>
                    )}
                  </div>
                  <div className="font-medium text-sm mt-2">{f.name}</div>
                  <div className="text-[12px] text-ink-muted mt-2 leading-relaxed">{f.notes}</div>
                  <div className="mt-4 flex items-center gap-4 text-[11px] uppercase tracking-wider text-ink-muted font-num">
                    {f.max_score != null && <span>Max <b className="text-ink">{f.max_score}</b></span>}
                    <span className="inline-flex items-center gap-1">
                      <History className="w-3 h-3" /> v<b className="text-ink">{f.active_version ?? "—"}</b>
                      {f.versions_count > 1 && <span className="text-[10px] opacity-70">({f.versions_count} total)</span>}
                    </span>
                  </div>
                </div>
                {f.editable && (
                  <ChevronRight className="w-5 h-5 text-ink-muted group-hover:text-brand transition-colors shrink-0" />
                )}
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
